import '../css/app.css'
import './bootstrap.js'
import { initializeTheme, syncBackofficeTheme } from '@/Composables/useAppearance'
import { currentHeliosIds, heliosVueErrorHandler, startHelios } from '@/helios'
import Bugsnag from '@bugsnag/js'
import BugsnagPluginVue from '@bugsnag/plugin-vue'
import { Head as InertiaHead, Link, createInertiaApp, router, usePage } from '@inertiajs/vue3'
import { i18nVue } from 'laravel-vue-i18n'
import { createApp, h } from 'vue'
import { ZiggyVue, route } from 'ziggy-js'

window.global ||= window

router.on('navigate', (event) => {
  const page = event.detail.page
  syncBackofficeTheme(page.component, page.props?.auth?.backoffice_ui)
})

router.on('httpException', (event) => {
  const status = event.detail.response?.status

  if (status === 401) {
    event.preventDefault()
    window.location.href = route('login')
  } else if (status === 419) {
    event.preventDefault()
    window.location.reload()
  }
})

startHelios()

// Lets Bugsnag errors be filtered by back office UI (backoffice.ui = v1/v2).
// Guests and Front pages have no backoffice_ui; the component is still useful.
const addBackofficeMetadata = (event) => {
  try {
    const page = usePage()

    if (!page?.component) {
      return
    }

    event.addMetadata('backoffice', {
      ui: page.props?.auth?.backoffice_ui ?? null,
      component: page.component,
    })
  } catch {
    // Reporting the original error matters more than this metadata.
  }
}

createInertiaApp({
  resolve: (name) => {
    const pages = import.meta.glob('./Pages/**/*.vue') // Lazy load pages
    return pages[`./Pages/${name}.vue`]()
  },
  title: (title) => {
    const page = usePage()
    const appName = page?.props?.app?.name || 'App'
    return title ? `${title} | ${appName}` : `${appName}`
  },
  progress: {
    // The delay after which the progress bar will appear
    // during navigation, in milliseconds.
    delay: 250,
    // The color of the progress bar.
    color: '#29d',
    // Whether to include the default NProgress styles.
    includeCSS: true,
    // Whether the NProgress spinner will be shown.
    showSpinner: false,
  },
  setup: async ({ el, App, props, plugin }) => {
    const vueApp = createApp({ render: () => h(App, props) })
      .use(plugin)
      .use(ZiggyVue)
      .use(i18nVue, {
        resolve: async (lang) => {
          const langs = import.meta.glob('../lang/*.json')
          const loadLocale = langs[`../lang/${lang}.json`]

          if (!loadLocale) {
            throw new Error(`Missing language file for locale: ${lang}`)
          }

          // Return the Vite JSON module `{ default: { ... } }`. laravel-vue-i18n's
          // avoidExceptionOnPromise uses `(await p).default`; a bare object would
          // become `{}` and break every translation.
          const locale = await loadLocale()

          // laravel-vue-i18n treats "" as a translation and renders blank text.
          // Drop untranslated entries so the English key shows instead.
          return {
            default: Object.fromEntries(Object.entries(locale.default).filter(([, message]) => message !== '')),
          }
        },
      })
      .component('InertiaLink', Link)
      .component('InertiaHead', InertiaHead)

    // Set before Bugsnag's plugin, which chains to the previous handler. Local dev without Bugsnag keeps Vue's own
    // handler (component trace, rethrow); the window error listeners still record the error.
    if (import.meta.env.VITE_BUGSNAG_API_KEY || !import.meta.env.DEV) {
      vueApp.config.errorHandler = heliosVueErrorHandler
    }

    if (import.meta.env.VITE_BUGSNAG_API_KEY) {
      Bugsnag.start({
        apiKey: import.meta.env.VITE_BUGSNAG_API_KEY,
        plugins: [new BugsnagPluginVue()],
        onError: (event) => {
          const errorClass = event.errors[0]?.errorClass
          const errorMessage = event.errors[0]?.errorMessage

          // Filter out InvalidError with undefined parameter
          // These come from New Relic agent and Echo/Pusher auth failures
          if (errorClass === 'InvalidError' && event.originalError === undefined) {
            return false
          }

          // Filter out AxiosError Network Errors
          // These are client-side network issues (connection drops, timeouts)
          if (errorClass === 'AxiosError' && errorMessage?.includes('Network Error')) {
            return false
          }

          addBackofficeMetadata(event)
          event.addMetadata('helios', currentHeliosIds())
        },
      })

      vueApp.use(Bugsnag.getPlugin('vue'))
    }

    vueApp.config.globalProperties.$filters = {
      translate(word) {
        return Lang.get(word)
      },
    }

    initializeTheme(props.initialPage.component, props.initialPage.props?.auth?.backoffice_ui)

    vueApp.mount(el)
  },
})
